import { ProjectTags } from '../ProjectTags'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { Theme, alpha, styled } from '@mui/material/styles'
import { useTranslation } from 'next-i18next'
import { Project } from '@/interfaces/projects'
import { OpenInWorkbenchButton } from '@/components/buttons/OpenInWorkbench'
import { ProjectCardFooter } from '@/components/projects/card/ProjectCardFooter'
import { ProjectCardThumbnail } from '@/components/projects/card/ProjectCardThumbnail'
import { ProjectTitle } from '@/components/projects/ProjectTitle'
import { ProjectAuthor } from '@/components/projects/ProjectAuthor'
import { getProjectHref, getUserHref } from '@/helpers/uri'

interface ProjectCardProps {
  project: Project
  priority?: boolean
}

const CardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hoverLabel'
})(({ theme, hoverLabel }: { theme?: Theme; hoverLabel: string }) => ({
  height: '100%',
  '& .MuiCard-root': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: `0 2px 15px ${alpha(theme.palette.common.black, 0.04)}`,
    transform: 'translateY(0)',
    transition: 'box-shadow .3s,transform .3s'
  },
  '& .MuiCardMedia-root': {
    position: 'relative',
    '& > .MuiBox-root': {
      transition: 'opacity .3s'
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: theme.palette.primary.main
    },
    '&:after': {
      content: `"${hoverLabel}"`,
      position: 'absolute',
      top: '55%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: theme.palette.common.white,
      fontSize: '1rem',
      fontWeight: 700,
      textShadow: `1px 1px 4px ${alpha(theme.palette.common.black, 0.75)}`,
      opacity: 0,
      transition: 'opacity .3s, top .3s'
    }
  },
  '&:hover': {
    '& .MuiCard-root': {
      boxShadow: `0 7px 30px ${alpha(theme.palette.common.black, 0.1)}`,
      transform: 'translateY(-8px)'
    },
    '& .MuiCardMedia-root': {
      '& > .MuiBox-root': {
        opacity: 0.2
      },
      '&:after': {
        opacity: 1,
        top: '50%'
      }
    },
    '& .OpenInWorkbenchButton': {
      color: theme.palette.common.white,
      border: `1px solid ${alpha(theme.palette.common.white, 0.55)}`,
      width: 145,
      '& > img': {
        filter: 'brightness(0) invert(1)'
      }
    }
  }
}))

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.grey['200']}`
}))

export const ProjectCard = ({ project, priority }: ProjectCardProps): React.ReactElement => {
  const { t } = useTranslation('projects')

  return (
    <CardWrapper hoverLabel={t('viewProject')}>
      <Card>
        <ProjectCardThumbnail
          href={getProjectHref(project.ownerName, project.publicProjectName)}
          thumbnailUrl={project.thumbnailUrl}
          title={project.projectName}
          priority={priority}
        />
        <CardContentStyled>
          <ProjectTitle
            href={getProjectHref(project.ownerName, project.publicProjectName)}
            title={project.projectName}
          />
          <ProjectAuthor href={getUserHref(project.ownerName)} author={project.ownerName} />
        </CardContentStyled>
        <ProjectCardFooter project={project} />
        <OpenInWorkbenchButton projectId={project.projectIdExt} projectName={project.projectName} />
        <ProjectTags isPrivate={!project.publicPermission?.readPreview} isShared={project.isSharedWithRequester} />
      </Card>
    </CardWrapper>
  )
}
